import type { AuthResponse } from 'server-schemas/channels';
import type { AirportsResponse } from 'server-schemas/channels/query';
import { FlightSearchBFFArguments, FlightSearchBFFResponse } from 'server-schemas/search';
import { FunnelQueryCookieObject, setCookie } from '../../../../cookie-utils';
import { fetchBff } from '../../fetch';
import { LABI_META, LABI_QUERY } from '../analytics/constants';
import { AIRPORTS_ENDPOINT, AUTH_STATUS_ENDPOINT, SEARCH_ENDPOINT } from './constants';
import { get as getPublicConfig } from '../../../../publicConfig';

const service = 'search';
export const TIMESTAMP_EXPIRY_MINUTES = 28;
export const FLIGHT_CALENDAR_OFFSETS = 1;

export const searchFlights = async (
  query: FunnelQueryCookieObject,
  outboundWindow = FLIGHT_CALENDAR_OFFSETS,
  outReturnWindow = FLIGHT_CALENDAR_OFFSETS
): Promise<FlightSearchBFFResponse> => {
  let labiQueryMeta = localStorage.getItem(LABI_META);
  const labiQuery = localStorage.getItem(LABI_QUERY);

  if (labiQueryTimeStampHasExpired(labiQuery)) {
    labiQueryMeta = null;
  }

  const body: FlightSearchBFFArguments = {
    journey: {
      outboundWindow,
      outboundDate: query.outboundDate,
      departureAirportOrMarketCode: query.originIata,
      arrivalAirportOrMarketCode: query.destinationIata,
      // If null, send undefined instead
      returnDate: query.returnDate || undefined,
      outReturnWindow
    },
    passengerMix: {
      ADT: query.numberOfAdults,
      CHD: query.numberOfChildren,
      INF: query.numberOfInfants
    },
    currencyCode: query.currencyCode,
    labiQueryMeta: labiQueryMeta || undefined
  };

  const data = await fetchBff<FlightSearchBFFResponse>(service, SEARCH_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(body)
  });
  setCookie('dlxSessionExpiry', (Date.now() + (Number(getPublicConfig().dlxSessionExpiry) || 1200000)).toString(), {
    path: '/',
    secure: true,
    sameSite: 'Lax'
  });

  if (data.metaData?.labiQueryMeta) {
    localStorage.setItem(LABI_META, data.metaData.labiQueryMeta);
  }

  if (data.metaData?.labiQuery) {
    localStorage.setItem(LABI_QUERY, data.metaData.labiQuery);
  }
  return data;
};

//timestamp check to ensure we don't get 404s from Look and book API - CDT-1719
//Look and book API has timestamp validation - the call to look and book API must be within 30 mins of the timestamp specified in the query
export const labiQueryTimeStampHasExpired = (labiQuery: string | null): boolean => {
  if (labiQuery) {
    const timestampRegEx = /(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})/;
    const regex = new RegExp(timestampRegEx);
    const matches = regex.exec(labiQuery);

    if (matches && matches.length > 0) {
      const timestampUTCFormat = matches[0].replace(timestampRegEx, '$1-$2-$3T$4:$5:$6Z');

      const timestampMilliseconds = new Date(timestampUTCFormat).getTime();
      const currentTimeMilliseconds = Date.now();

      const diffMilliseconds = currentTimeMilliseconds - timestampMilliseconds;
      const diffMinutes = Math.round(diffMilliseconds / 1000) / 60;

      return diffMinutes >= TIMESTAMP_EXPIRY_MINUTES;
    }
  }
  return false;
};

export const fetchAuthenticationStatus = async (): Promise<AuthResponse> => {
  return await fetchBff<AuthResponse>(service, `${AUTH_STATUS_ENDPOINT}`, { method: 'GET' });
};

export const fetchValidRoutes = () => fetchBff<AirportsResponse>(service, AIRPORTS_ENDPOINT, { method: 'GET' });
